import store from '../store/store';
import { firebase } from '../config/configureFirebase';
import { patchUsersCacheUpsert } from './usersactions';
import { postWithIdToken } from './mileactions';
import { update, set, child, push, get } from "firebase/database";
import { uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { signInWithCustomToken } from "firebase/auth";

// Onboarding wizard actions (mobile). These are plain async helpers, not
// redux thunks: the wizard drives its own step state and needs to KNOW when
// a write has landed before it re-evaluates the live profile on "Continue".
// The existing updateProfile thunk is fire-and-forget, which is fine for
// background edits but would let the wizard advance on a write that has not
// reached the server yet on a patchy 3G link — so everything here is awaited.

// Firebase queues RTDB writes indefinitely while offline, so an awaited
// update()/set() would spin forever on a dead link. Reject after ms so the
// wizard can show its network-error UI (isNetworkError matches the
// 'write_timeout' code / "timed out" message). The queued write is NOT
// cancelled: it still lands when connectivity returns, and callers always
// re-evaluate from the live profile rather than trusting local state, so a
// late-landing write is harmless.
const withWriteTimeout = (promise, ms = 15000) =>
    new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            const err = new Error('Write timed out');
            err.code = 'write_timeout';
            reject(err);
        }, ms);
        promise.then(
            (value) => { clearTimeout(timer); resolve(value); },
            (error) => { clearTimeout(timer); reject(error); }
        );
    });

const currentUid = () => {
    const { auth } = firebase;
    const user = auth && auth.currentUser;
    if (!user) {
        const err = new Error('Not signed in');
        err.code = 'not_signed_in';
        throw err;
    }
    return user.uid;
};

// POST /complete_onboarding. The server validates the stored profile, binds
// the generated email to Firebase Auth, stamps completion and (drivers on the
// flat-rate plan) grants the welcome credit. requestId is minted once per
// wizard session (makeMileRequestId) and reused across retries so the server
// replay guard engages. Throws Error with .code / .payload (see postWithIdToken).
export const completeOnboarding = (requestId) =>
    postWithIdToken('/complete_onboarding', { requestId });

// complete_onboarding binds a login email to a brand-new account's Firebase
// Auth record. Firebase treats that as a major account change and revokes the
// device's refresh token, so the session would die at its next token refresh
// (within the hour, or at the next cold start) and the user would land on
// Login again. The server returns a one-time custom token in that case; signing
// in with it replaces the revoked session for the SAME uid without any user
// interaction (onAuthStateChanged does not fire again for an unchanged uid, so
// nothing downstream restarts). Rejects on network failure — the caller
// retries, and on final failure the old session simply carries on as before.
export const adoptSessionToken = async (token) => {
    const { auth } = firebase;
    if (!token || !auth) return false;
    await signInWithCustomToken(auth, token);
    return true;
};

// Awaited multi-path update on the caller's own /users/{uid} node. Unlike
// updateProfile (fire-and-forget thunk) the promise resolves only after the
// write is acknowledged (or withWriteTimeout gives up), so the wizard can
// safely re-read the live profile.
export const persistOnboardingFields = async (fields) => {
    const { singleUserRef } = firebase;
    const uid = currentUid();
    await withWriteTimeout(update(singleUserRef(uid), fields));
};

// Mint a /cars push key WITHOUT writing anything. The wizard stores it in
// its AsyncStorage draft so a resume after a crash never creates a second
// car row; the row itself is only written by saveOnboardingCar.
export const newOnboardingCarId = () => {
    const { carAddRef } = firebase;
    return push(carAddRef).key;
};

const uploadRefForKind = (kind, opts) => {
    const {
        driverDocsRef,
        driverDocsRefBack,
        insuranceImageRef,
        verifyIdImageRef,
        profileImageRef,
        carImage
    } = firebase;
    const uid = currentUid();
    switch (kind) {
        case 'licenseImage':
            return driverDocsRef(uid);
        case 'licenseImageBack':
            return driverDocsRefBack(uid);
        case 'insuranceImage':
            return insuranceImageRef(uid);
        case 'verifyIdImage':
            return verifyIdImageRef(uid);
        case 'profileImage':
            return profileImageRef(uid);
        case 'carImage':
            if (!opts || !opts.carId) {
                throw new Error('startOnboardingUpload: carImage requires opts.carId');
            }
            return carImage(opts.carId);
        default:
            throw new Error('startOnboardingUpload: unknown kind ' + kind);
    }
};

// Starts a RESUMABLE upload and returns { task, url }.
//  - task: the firebase UploadTask. The wizard subscribes with
//    task.on('state_changed') for the progress chip and runs a 90 s
//    no-progress watchdog that calls task.cancel() before retrying with a
//    fresh blob. A plain uploadBytes() would give us neither progress nor
//    a cancel handle, and on 3G that means an unbounded spinner.
//  - url: Promise<string> resolving to the download URL once the upload
//    completes (closing the RN blob if it can be closed), rejecting on
//    error or cancel. Kept separate from `task` so callers can await the
//    URL while still holding the task for cancellation.
export const startOnboardingUpload = (kind, blob, opts) => {
    const storageRef = uploadRefForKind(kind, opts);
    const task = uploadBytesResumable(storageRef, blob);
    const url = new Promise((resolve, reject) => {
        task.on(
            'state_changed',
            null,
            (error) => reject(error),
            () => {
                if (blob && typeof blob.close === 'function') {
                    try { blob.close(); } catch (_) { /* already closed */ }
                }
                getDownloadURL(storageRef).then(resolve, reject);
            }
        );
    });
    return { task, url };
};

// Removes cars/{carId} from Storage after the Vision check said "not a car"
// so the rejected photo does not linger as an orphan object. A missing
// object is not an error here (upload may have been cancelled midway).
export const deleteOnboardingCarImage = async (carId) => {
    const { carImage } = firebase;
    try {
        await deleteObject(carImage(carId));
    } catch (error) {
        if (!error || error.code !== 'storage/object-not-found') {
            throw error;
        }
    }
};

// Writes the wizard's vehicle draft as the driver's active car and mirrors
// the vehicle fields onto /users/{uid}, exactly like caractions
// updateUserCarWithImage (:117-155) — the car photo is already in Storage
// (startOnboardingUpload), so this only does the two RTDB writes. `url` may
// be null: the photo is optional in the wizard ("Continue without photo"),
// and a null car_image is simply absent on the row / the profile — every
// reader already falls back (CarsScreen placeholder, taskactions ''). Both are
// awaited (with withWriteTimeout so an offline device errors instead of
// hanging): the wizard's "Continue" re-reads the live profile for carType /
// vehicleNumber and must not race the write.
export const saveOnboardingCar = async ({ carId, url, carType, vehicleNumber, vehicleMake, vehicleModel, settings, profile }) => {
    const { carAddRef, carsRef, singleUserRef } = firebase;
    const uid = currentUid();
    const cfg = settings || store.getState().settingsdata.settings || {};
    const approved = cfg.carApproval !== true;
    const now = Date.now();
    // The smart gate routes EXISTING drivers with an incomplete profile into
    // the wizard too, and a legacy driver re-running the vehicle block may
    // already own an active car from the old signup / car-manager flow. The
    // app treats "active" as singular (the driver's current vehicle mirrored
    // onto /users), so deactivate every other active car first — like
    // caractions.updateUserCar does when switching cars — or the driver ends
    // up with two active rows and the wrong one can be picked. carsRef(uid,
    // 'driver') is the indexed /cars query on driver == uid (cheap: only
    // this driver's rows). The wizard-minted carId is skipped so a resumed
    // draft that already wrote its row is not toggled off.
    const othersSnap = await withWriteTimeout(get(carsRef(uid, 'driver')));
    const deactivations = [];
    othersSnap.forEach((carSnap) => {
        const car = carSnap.val();
        if (car && car.active === true && carSnap.key !== carId) {
            deactivations.push(update(child(carAddRef, carSnap.key), { active: false }));
        }
    });
    if (deactivations.length) {
        await withWriteTimeout(Promise.all(deactivations));
    }
    const carData = {
        carType,
        vehicleNumber,
        vehicleMake,
        vehicleModel,
        other_info: '',
        car_image: url || null,
        active: true,
        approved,
        createdAt: now,
        driver: uid,
        fleetadmin: (profile && profile.fleetadmin) || null
    };
    await withWriteTimeout(set(child(carAddRef, carId), carData));
    const userPatch = {
        carType,
        vehicleNumber,
        vehicleMake,
        vehicleModel,
        other_info: '',
        car_image: url || null,
        carApproved: approved,
        updateAt: now
    };
    await withWriteTimeout(update(singleUserRef(uid), userPatch));
    // No-op on RN (no IDB users cache); keeps a web caller's cached users
    // array coherent, same as the caractions writers.
    patchUsersCacheUpsert(uid, userPatch);
};
